import React, { useState } from 'react'
import * as yup from 'yup'
import { useIntl } from 'react-intl'
import { useFormik } from 'formik'
import { toast } from 'react-toastify'
import {
    Box,
    CircularProgress,
    DialogContent,
    FormHelperText,
    SelectChangeEvent,
    DialogTitle,
    DialogActions,
    Dialog,
    Button,
    Typography,
} from '@mui/material'
import {
    requiredArray,
    requiredString,
    requiredId,
} from '../../../../utils/validation'
import { H6 } from '../../../../layouts/TextStyles'
import CustomInput from '../../../../components/muiComponents/customInput'
import MultipleSelect from '../../../../components/muiComponents/multipleSelect/MultipleSelect'
import SingleSelect from '../../../../components/muiComponents/singleSelect/SingleSelect'
import { courseApiSlice } from '../../../../features/course/courseSlice'
import { teacherApiSlice } from '../../../../features/teacher/teacherSlice'
import { courseSubjectApiSlice } from '../../../../features/course/courseSubjectSlice'
import { GroupState } from '../../../../features/types'
import { groupApiSlice } from '../../../../features/group/groupSlice'
import SelectedSubject from './SelectedSubject'
import { tone } from '../../../../utils/uiTokens'

const styles = {
    form: {
        display: 'flex',
        justifyContent: 'space-between',
    },

    error: {
        color: tone.bad.text,
        marginLeft: 2,
    },
}
const AddGroupDialog = () => {
    const { formatMessage } = useIntl()
    const [open, setOpen] = useState(false)
    const [selectedCourseSubjects, setSelectedCourseSubjects] = useState([])
    const [createGroup, { isLoading: isCreating }] =
        groupApiSlice.useCreateGroupMutation()
    const { data: courses = [] } = courseApiSlice.useFetchAllCoursesQuery(
        undefined,
        { skip: !open },
    )
    const { curators = [] } = teacherApiSlice.useFetchAllTeachersQuery(
        {
            query: `page=${0}&size=${10000}`,
            roles: ['CURATOR'],
        },
        {
            skip: !open,
            selectFromResult: ({ data }) => ({
                curators: data?.data,
            }),
        },
    )

    const group: GroupState = {
        id: null,
        title: '',
        languageEnum: '',
        courseId: '',
        curatorIds: [],
        subjects: [],
    }

    const validationSchema = yup.object().shape({
        title: requiredString({ formatMessage }),
        courseId: requiredId({ formatMessage }),
        subjects: requiredArray({ formatMessage }),
    })

    const formik = useFormik({
        initialValues: group,
        validationSchema: validationSchema,
        validateOnChange: false,
        validateOnBlur: false,

        onSubmit: async () => {
            handleCreateGroup()
        },
    })

    const handleCreateGroup = async () => {
        try {
            await createGroup(formik.values).unwrap()
            toast.success(formatMessage({ id: 'savedSuccess' }))
            formik.resetForm()
            setSelectedCourseSubjects([])
            handleClose()
        } catch (error) {}
    }

    const { data: activeCourseSubjects = [] } =
        courseSubjectApiSlice.useFetchActiveCourseSubjectsQuery(
            Number(formik.values.courseId),
            { skip: !formik.values.courseId },
        )

    const handleCourseChange = (
        event: SelectChangeEvent<typeof formik.values.courseId>,
    ) => {
        const {
            target: { value },
        } = event

        formik.setFieldValue('courseId', value)
        setSelectedCourseSubjects([])
    }

    const handleSubjectChange = (
        event: SelectChangeEvent<typeof selectedCourseSubjects>,
    ) => {
        const {
            target: { value },
        } = event
        if(value?.length <= 1){
            setSelectedCourseSubjects(
                typeof value === 'string' ? value.split(',') : value,
            )    
        } else {
            toast.warn("Тек 1 пән енгізе аласыз")
        }
    }

    const handleLanguageChange = (
        event: SelectChangeEvent<typeof formik.values.languageEnum>,
    ) => {
        const {
            target: { value },
        } = event

        formik.setFieldValue('languageEnum', value)
    }

    const handleCuratorsChange = (
        event: SelectChangeEvent<typeof formik.values.curatorIds>,
    ) => {
        const {
            target: { value },
        } = event

        formik.setFieldValue(
            'curatorIds',
            typeof value === 'string' ? value.split(',') : value,
        )
    }

    const handleClickOpen = () => {
        setOpen(true)
    }

    const handleClose = () => {
        setOpen(false)
    }

    return (
        <div>
            <Button
                variant={'contained'}
                onClick={handleClickOpen}
            >
                {formatMessage({ id: 'addGroup' })}
            </Button>

            <Dialog
                maxWidth={'lg'}
                open={open}
                onClose={handleClose}
                fullWidth
            >
                <DialogTitle id="responsive-dialog-title">
                    {formatMessage({ id: 'addGroup' })}
                </DialogTitle>

                <DialogContent>
                    <form
                        style={styles.form}
                        onSubmit={formik.handleSubmit}
                    >
                        <Box sx={{ width: '100%', mr: '10px' }}>
                            <Box mb={2}>
                                <H6>{formatMessage({ id: 'titleName' })}</H6>

                                <CustomInput
                                    id="title"
                                    name="title"
                                    placeholder={formatMessage({
                                        id: 'titleName',
                                    })}
                                    fullWidth
                                    value={formik.values.title}
                                    onChange={formik.handleChange}
                                    onBlur={formik.handleBlur}
                                    helperText={formik.errors.title}
                                    error={Boolean(formik.errors.title)}
                                    margin="dense"
                                />
                            </Box>

                            <Box mb={2}>
                                <H6>{formatMessage({ id: 'language' })}</H6>

                                <SingleSelect
                                    id="languageEnum"
                                    name="languageEnum"
                                    placeholder={formatMessage({
                                        id: 'language',
                                    })}
                                    value={formik.values.languageEnum}
                                    handleChange={handleLanguageChange}
                                    menuItems={[
                                        { id: 'RU', name: 'RU' },
                                        { id: 'KZ', name: 'KZ' },
                                    ]}
                                />
                            </Box>

                            <Box mb={2}>
                                <H6>{formatMessage({ id: 'course' })}</H6>

                                <SingleSelect
                                    id="courseId"
                                    name="courseId"
                                    placeholder={formatMessage({
                                        id: 'course',
                                    })}
                                    value={formik.values.courseId}
                                    menuItems={courses}
                                    handleChange={handleCourseChange}
                                    handleBlur={formik.handleBlur}
                                    handleError={Boolean(
                                        formik.errors.courseId,
                                    )}
                                />

                                <FormHelperText sx={styles.error}>
                                    {formik.touched.courseId &&
                                        formik.errors.courseId}
                                </FormHelperText>
                            </Box>

                            <Box mb={2}>
                                <H6>{formatMessage({ id: 'curators' })}</H6>

                                <MultipleSelect
                                    id="curatorIds"
                                    name="curatorIds"
                                    value={formik.values.curatorIds}
                                    handleChange={handleCuratorsChange}
                                    handleBlur={formik.handleBlur}
                                    menuItems={curators}
                                    multiple={true}
                                    searchable={true}
                                    placeholder={formatMessage({
                                        id: 'curators',
                                    })}
                                    handleError={Boolean(
                                        formik.errors.curatorIds,
                                    )}
                                />

                                <FormHelperText sx={styles.error}>
                                    {formik.touched.curatorIds &&
                                        formik.errors.curatorIds}
                                </FormHelperText>
                            </Box>

                            <Box mb={2}>
                                <H6>{formatMessage({ id: 'subjects' })}</H6>

                                <MultipleSelect
                                    id="subjects"
                                    name="subjects"
                                    value={selectedCourseSubjects}
                                    handleChange={handleSubjectChange}
                                    handleBlur={formik.handleBlur}
                                    menuItems={activeCourseSubjects}
                                    multiple={true}
                                    placeholder={formatMessage({
                                        id: 'selectSubject',
                                    })}
                                    handleError={
                                        Boolean(formik.errors.subjects) &&
                                        selectedCourseSubjects.length === 0
                                    }
                                />

                                <FormHelperText sx={styles.error}>
                                    {formik.submitCount &&
                                    selectedCourseSubjects.length === 0 ? (
                                        <Typography variant="inherit">
                                            {formatMessage(
                                                {
                                                    id: 'mustOptionSelected',
                                                    defaultMessage:
                                                        'You must select at least { value } option',
                                                },
                                                { value: 1 },
                                            )}
                                        </Typography>
                                    ) : null}
                                </FormHelperText>
                            </Box>
                        </Box>

                        <Box sx={{ width: '100%', ml: '10px' }}>
                            {selectedCourseSubjects?.map(
                                (courseSubjectId, index) => (
                                    <SelectedSubject
                                        key={courseSubjectId}
                                        courseSubjectId={courseSubjectId}
                                        index={index}
                                        courseSubjects={activeCourseSubjects}
                                        formik={formik}
                                    />
                                ),
                            )}
                        </Box>
                    </form>
                </DialogContent>

                <DialogActions sx={{ m: 2, mt: 0 }}>
                    <Button
                        onClick={handleClose}
                        variant={'outlined'}
                        sx={{ mr: 1 }}
                        disabled={isCreating}
                    >
                        {formatMessage({ id: 'cancel' })}
                    </Button>

                    <Button
                        variant={'contained'}
                        sx={{ width: '120px' }}
                        onClick={() => formik.handleSubmit()}
                    >
                        {isCreating ? (
                            <CircularProgress
                                size={20}
                                style={{ marginRight: '8px' }}
                            />
                        ) : (
                            formatMessage({ id: 'save' })
                        )}
                    </Button>
                </DialogActions>
            </Dialog>
        </div>
    )
}

export default AddGroupDialog
